import React, { useState, useEffect } from 'react';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';

import clientRequirementsService from '../../../services/clientRequirementsService';
import jobApplicationService from '../../../services/jobApplicationService';

const MatchingEnginePanel = ({
  requirement,
  isOpen = false,
  onClose
}) => {
  const [matches, setMatches] = useState([]);
  const [allApplicants, setAllApplicants] = useState([]);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [selectedApplicants, setSelectedApplicants] = useState([]);
  const [bulkNotes, setBulkNotes] = useState('');
  const [matchFilters, setMatchFilters] = useState({
    minScore: 0,
    status: '',
    experience: '',
    location: ''
  });

  useEffect(() => {
    if (isOpen && requirement) {
      loadMatches();
      loadApplicants();
    }
  }, [isOpen, requirement]);

  const loadMatches = async () => {
    if (!requirement?.id) return;
    
    try {
      setLoading(true);
      const result = await clientRequirementsService.getMatchingCandidates(requirement.id);
      
      if (result.success) {
        setMatches(result.data);
      }
    } catch (err) {
      console.log('Failed to load matches:', err);
    } finally {
      setLoading(false);
    }
  };

  const loadApplicants = async () => {
    try {
      const result = await jobApplicationService.getApplications();
      
      if (result.success) {
        // Filter out applicants that are already matched
        const existingMatches = matches.map(m => m.applicant_id);
        const availableApplicants = result.data.filter(app => 
          !existingMatches.includes(app.id) && app.status !== 'rejected'
        );
        setAllApplicants(availableApplicants);
      }
    } catch (err) {
      console.log('Failed to load applicants:', err);
    }
  };

  const calculateCompatibilityScore = (applicant, requirement) => {
    let score = 0;
    let factors = 0;

    // Skill matching (40% of score)
    const requiredSkills = requirement.required_skills || [];
    const applicantSkills = (applicant.notes || '').toLowerCase();
    
    const skillMatches = requiredSkills.filter(skill => 
      applicantSkills.includes(skill.toLowerCase())
    ).length;
    
    if (requiredSkills.length > 0) {
      score += (skillMatches / requiredSkills.length) * 40;
      factors++;
    }

    // Position matching (30% of score)
    const reqPosition = requirement.position_title?.toLowerCase() || '';
    const appPosition = applicant.position?.toLowerCase() || '';
    
    if (reqPosition && appPosition) {
      const positionMatch = appPosition.includes(reqPosition) || reqPosition.includes(appPosition);
      score += positionMatch ? 30 : 0;
      factors++;
    }

    // Location proximity (20% of score)
    const reqLocation = requirement.location?.toLowerCase() || '';
    const appLocation = applicant.phone || ''; // Using phone as location placeholder
    
    if (reqLocation && appLocation) {
      // Simplified location matching - in real app would use proper location data
      score += reqLocation.includes('mumbai') && appLocation.includes('98765') ? 20 : 10;
      factors++;
    }

    // Random factor for demo (10%)
    score += Math.random() * 10;

    return Math.min(Math.round(score), 100);
  };

  const findPotentialMatches = () => {
    if (!requirement || allApplicants.length === 0) return [];

    return allApplicants
      .map(applicant => ({
        ...applicant,
        compatibility_score: calculateCompatibilityScore(applicant, requirement),
        skill_match_percentage: Math.floor(Math.random() * 40) + 60, // Mock skill match
        experience_match: Math.random() > 0.5,
        salary_match: Math.random() > 0.3,
        location_match: Math.random() > 0.4
      }))
      .filter(applicant => applicant.compatibility_score >= matchFilters.minScore)
      .sort((a, b) => b.compatibility_score - a.compatibility_score)
      .slice(0, 10); // Top 10 matches
  };

  const handleCreateMatch = async (applicant) => {
    try {
      setSubmitting(true);
      
      const matchData = {
        requirement_id: requirement.id,
        applicant_id: applicant.id,
        compatibility_score: applicant.compatibility_score,
        skill_match_percentage: applicant.skill_match_percentage,
        experience_match: applicant.experience_match,
        salary_match: applicant.salary_match,
        location_match: applicant.location_match,
        matching_status: 'pending',
        hr_notes: 'Auto-matched by system'
      };

      const result = await clientRequirementsService.createCandidateMatch(matchData);
      
      if (result.success) {
        loadMatches();
        loadApplicants();
      }
    } catch (err) {
      console.log('Failed to create match:', err);
    } finally {
      setSubmitting(false);
    }
  };

  const handleBulkSubmit = async () => {
    if (selectedApplicants.length === 0) return;

    try {
      setSubmitting(true);
      
      const promises = selectedApplicants.map(applicantId => {
        const applicant = allApplicants.find(a => a.id === applicantId);
        if (!applicant) return null;

        const matchData = {
          requirement_id: requirement.id,
          applicant_id: applicant.id,
          compatibility_score: calculateCompatibilityScore(applicant, requirement),
          skill_match_percentage: Math.floor(Math.random() * 40) + 60,
          experience_match: Math.random() > 0.5,
          salary_match: Math.random() > 0.3,
          location_match: Math.random() > 0.4,
          matching_status: 'submitted',
          hr_notes: bulkNotes || 'Bulk submitted to client'
        };

        return clientRequirementsService.createCandidateMatch(matchData);
      }).filter(Boolean);

      await Promise.all(promises);
      
      loadMatches();
      loadApplicants();
      setSelectedApplicants([]);
      setBulkNotes('');
    } catch (err) {
      console.log('Failed to bulk submit:', err);
    } finally {
      setSubmitting(false);
    }
  };

  const handleUpdateMatchStatus = async (matchId, status, notes = '') => {
    try {
      const result = await clientRequirementsService.updateMatchStatus(matchId, status, notes);
      
      if (result.success) {
        loadMatches();
      }
    } catch (err) {
      console.log('Failed to update match status:', err);
    }
  };

  if (!isOpen || !requirement) return null;

  const potentialMatches = findPotentialMatches();
  
  const getScoreColor = (score) => {
    if (score >= 80) return 'text-green-600 bg-green-100';
    if (score >= 60) return 'text-blue-600 bg-blue-100';
    if (score >= 40) return 'text-yellow-600 bg-yellow-100';
    return 'text-red-600 bg-red-100';
  };

  const getStatusBadge = (status) => {
    const statusStyles = {
      pending: 'bg-yellow-100 text-yellow-800',
      submitted: 'bg-blue-100 text-blue-800',
      approved: 'bg-green-100 text-green-800',
      rejected: 'bg-red-100 text-red-800',
      interviewing: 'bg-purple-100 text-purple-800',
      selected: 'bg-green-100 text-green-800'
    };

    return (
      <span className={`inline-flex items-center px-2 py-1 rounded text-xs font-medium ${statusStyles[status] || statusStyles.pending}`}>
        {status?.replace('_', ' ') || 'Pending'}
      </span>
    );
  };

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex min-h-screen items-end justify-center px-4 pt-4 pb-20 text-center sm:block sm:p-0">
        {/* Background overlay */}
        <div 
          className="fixed inset-0 bg-black bg-opacity-50 transition-opacity"
          onClick={onClose}
        ></div>

        {/* Panel */}
        <div className="relative inline-block transform overflow-hidden rounded-lg bg-background text-left align-bottom shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-6xl sm:align-middle">
          {/* Header */}
          <div className="border-b bg-background px-6 py-4">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-xl font-semibold text-foreground">
                  Candidate Matching Engine
                </h3>
                <p className="text-muted-foreground mt-1">
                  {requirement.title} • {requirement.clients?.company_name}
                </p>
              </div>
              <Button
                variant="outline"
                size="sm"
                iconName="X"
                onClick={onClose}
              />
            </div>
          </div>

          {/* Content */}
          <div className="px-6 py-6 max-h-96 overflow-y-auto">
            <div className="grid grid-cols-1 xl:grid-cols-2 gap-8">
              {/* Existing Matches */}
              <div>
                <div className="flex items-center justify-between mb-4">
                  <h4 className="text-lg font-medium text-foreground">
                    Current Matches ({matches.length})
                  </h4>
                </div>

                {loading ? (
                  <div className="text-center py-8">
                    <div className="animate-spin h-8 w-8 border-4 border-primary border-t-transparent rounded-full mx-auto"></div>
                    <p className="text-muted-foreground mt-2">Loading matches...</p>
                  </div>
                ) : matches.length === 0 ? (
                  <div className="text-center py-8 bg-muted/20 rounded-lg">
                    <p className="text-muted-foreground">No matches created yet</p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {matches.map((match) => (
                      <div key={match.id} className="bg-card border rounded-lg p-4">
                        <div className="flex items-start justify-between mb-2">
                          <div>
                            <h5 className="font-medium text-foreground">
                              {match.job_applications?.applicant_name}
                            </h5>
                            <p className="text-sm text-muted-foreground">
                              {match.job_applications?.position}
                            </p>
                          </div>
                          <div className="flex items-center gap-2">
                            <span className={`px-2 py-1 rounded text-sm font-medium ${getScoreColor(match.compatibility_score)}`}>
                              {match.compatibility_score}%
                            </span>
                            {getStatusBadge(match.matching_status)}
                          </div>
                        </div>
                        
                        <div className="grid grid-cols-2 gap-4 text-xs text-muted-foreground mb-3">
                          <div>Skill Match: {match.skill_match_percentage}%</div>
                          <div>Experience: {match.experience_match ? '✓' : '✗'}</div>
                          <div>Salary: {match.salary_match ? '✓' : '✗'}</div>
                          <div>Location: {match.location_match ? '✓' : '✗'}</div>
                        </div>

                        <div className="flex items-center gap-2 pt-2 border-t">
                          <Button 
                            size="sm" 
                            variant="outline"
                            onClick={() => handleUpdateMatchStatus(match.id, 'submitted', 'Submitted to client for review')}
                          >
                            Submit to Client
                          </Button>
                          <Button 
                            size="sm" 
                            variant="outline"
                            onClick={() => handleUpdateMatchStatus(match.id, 'rejected', 'Not suitable for this requirement')}
                          >
                            Reject
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Potential Matches */}
              <div>
                <div className="flex items-center justify-between mb-4">
                  <h4 className="text-lg font-medium text-foreground">
                    Potential Matches
                  </h4>
                  <div className="flex items-center gap-2">
                    <Input
                      type="number"
                      placeholder="Min score"
                      value={matchFilters.minScore}
                      onChange={(e) => setMatchFilters(prev => ({ ...prev, minScore: parseInt(e.target.value) || 0 }))}
                      className="w-24"
                    />
                  </div>
                </div>

                {/* Bulk Actions */}
                {selectedApplicants.length > 0 && (
                  <div className="bg-primary/10 border border-primary/20 rounded-lg p-4 mb-4">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-sm font-medium">
                        {selectedApplicants.length} candidate(s) selected
                      </span>
                      <Button 
                        size="sm" 
                        onClick={handleBulkSubmit}
                        loading={submitting}
                      >
                        Submit to Client
                      </Button>
                    </div>
                    <Input
                      placeholder="Add notes for bulk submission..."
                      value={bulkNotes}
                      onChange={(e) => setBulkNotes(e.target.value)}
                      className="text-sm"
                    />
                  </div>
                )}

                <div className="space-y-4">
                  {potentialMatches.map((applicant) => (
                    <div key={applicant.id} className="bg-card border rounded-lg p-4">
                      <div className="flex items-start justify-between mb-2">
                        <div className="flex items-center gap-3">
                          <input
                            type="checkbox"
                            checked={selectedApplicants.includes(applicant.id)}
                            onChange={(e) => {
                              if (e.target.checked) {
                                setSelectedApplicants(prev => [...prev, applicant.id]);
                              } else {
                                setSelectedApplicants(prev => prev.filter(id => id !== applicant.id));
                              }
                            }}
                            className="rounded border-border"
                          />
                          <div>
                            <h5 className="font-medium text-foreground">
                              {applicant.applicant_name}
                            </h5>
                            <p className="text-sm text-muted-foreground">
                              {applicant.position}
                            </p>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <span className={`px-2 py-1 rounded text-sm font-medium ${getScoreColor(applicant.compatibility_score)}`}>
                            {applicant.compatibility_score}%
                          </span>
                        </div>
                      </div>
                      
                      <div className="grid grid-cols-2 gap-4 text-xs text-muted-foreground mb-3">
                        <div>Skill Match: {applicant.skill_match_percentage}%</div>
                        <div>Experience: {applicant.experience_match ? '✓' : '✗'}</div>
                        <div>Salary: {applicant.salary_match ? '✓' : '✗'}</div>
                        <div>Location: {applicant.location_match ? '✓' : '✗'}</div>
                      </div>

                      <div className="flex items-center gap-2 pt-2 border-t">
                        <Button 
                          size="sm" 
                          variant="outline"
                          onClick={() => handleCreateMatch(applicant)}
                          loading={submitting}
                        >
                          Add Match
                        </Button>
                        <Button 
                          size="sm" 
                          variant="outline"
                        >
                          View Profile
                        </Button>
                      </div>
                    </div>
                  ))}

                  {potentialMatches.length === 0 && (
                    <div className="text-center py-8 bg-muted/20 rounded-lg">
                      <p className="text-muted-foreground">No potential matches found</p>
                      <p className="text-sm text-muted-foreground mt-1">
                        Try lowering the minimum score or check available candidates
                      </p>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Footer */}
          <div className="border-t bg-muted/20 px-6 py-4">
            <div className="flex items-center justify-between">
              <div className="text-sm text-muted-foreground">
                {matches.length} matches created • {potentialMatches.length} potential matches
              </div>
              <div className="flex items-center gap-3">
                <Button variant="outline" onClick={onClose}>
                  Close
                </Button>
                <Button iconName="Zap" iconPosition="left">
                  Auto-Match All
                </Button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MatchingEnginePanel;